import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { PeopleFilterFields, PeopleSystemColumns } from 'src/app/people/models/peopleFilterFields';
import { FormDetailsReportSystemColumns } from 'src/app/reports/form-details-report/form-details-report.fields';
import { TeamRostersSystemColumns } from 'src/app/reports/team-rosters-report/team-rosters-report-fields';
import { TeamSurveyDetailsReportSystemColumns } from 'src/app/reports/team-survey-details-report/team-survey-details-report.fields';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeEntityType } from 'src/app/shared/models/attributes/attributeEntityType';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { AttributeService } from 'src/app/shared/services/attribute.service';
import { SurveyDefinitionService } from 'src/app/shared/services/survey-definition.service';
import { TeamSystemColumns } from 'src/app/teams/teams-home/team-regular-fields';
import { ItemPickerModel } from '../../item-picker/models/itemPickerModel';
import { CustomViewColumn } from '../../odata-filter/models/custom-view-column';
import { ViewContext } from '../../odata-filter/models/view-context';
import { WorkspaceService } from 'src/app/shared/services/workspace.service';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { CredentialingDetailsReportSystemColumns } from 'src/app/reports/credentialing-details-report/credentialing-details-report.fields';
import {
  CredentialPaymentRecordsReportSystemColumns,
  PaymentRecordsReportSystemColumns,
} from 'src/app/reports/payment-records-report/payment-records-report.fields';
import { TenantModuleType } from 'src/app/shared/models/tenantModuleType';
import { AllInvoiceReportSystemColumns } from 'src/app/reports/all-invoice-report/all-invoice-report.fields';
import { OpportunityBoardReportSystemColumns } from 'src/app/reports/opportunity-board-report/opportunity-board-report-fields';
import { PersonScenarioReportComponent } from 'src/app/reports/person-scenario-report/person-scenario-report.component';
import { PersonScenarioReportSystemColumns } from 'src/app/reports/person-scenario-report/person-scenario-report.fields';

@Component({
  selector: 'lineup-manage-columns',
  templateUrl: './manage-columns.component.html',
  styleUrls: ['./manage-columns.component.scss'],
})
export class ManageColumnsComponent implements OnInit, OnDestroy {
  @Input() columns: CustomViewColumn[] = [];
  @Input() viewContext: ViewContext;
  itemPickerList: ItemPickerModel[] = [];
  private attributes: AttributeDefinition[] = [];
  private subs: Subscription[] = [];
  maxWorkspaceLimit;

  constructor(
    private attributeService: AttributeService,
    private surveyDefinitionService: SurveyDefinitionService,
    private appConfigService: AppConfigService,
    private workspaceService: WorkspaceService
  ) {}

  get canManageColumns(): boolean {
    return this.appConfigService.tenant.plan.canManageColumns;
  }

  ngOnInit(): void {
    this.maxWorkspaceLimit = this.appConfigService.tenant.plan
      ? this.appConfigService.tenant.plan.maxWorkspaceLimit
      : 0;

    if (this.viewContext == ViewContext.TeamSurvey) {
      this.subs.push(
        this.surveyDefinitionService.surveyDefinitionAttributes
          .pipe(
            map((result) => {
              if (!result) return null;
              return result;
            })
          )
          .subscribe((attributes) => {
            if (!attributes) return;
            this.attributes = attributes;
            this.createItemPickerList();
          })
      );
    } else {
      this.subs.push(
        this.attributeService.attributes
          .pipe(
            map((attributes) => {
              if (!attributes) return null;
              return attributes.filter((x) => x.entityType != AttributeEntityType.Survey);
            })
          )
          .subscribe((attributes) => {
            if (!attributes) return;
            this.attributes = attributes;
            this.createItemPickerList();
          })
      );
    }
  }
  onAddColumn(itemPicker: ItemPickerModel) {
    if (!this.canManageColumns) return;
    let column = new CustomViewColumn();
    let customField = this.attributes.find((x) => x.id == itemPicker.id);
    if (customField) {
      column.isCustomField = true;
      column.isComplexCustomField = customField.uiType == AttributeUiType.Complex;
      column.customFieldId = itemPicker.id;
      column.customFieldEntityType = AttributeEntityType[customField.entityType];
    } else {
      column.systemName = itemPicker.id;
    }
    column.rank = this.columns.length;
    column.displayName = itemPicker.name;

    this.columns.push(column);
    this.createItemPickerList();
  }
  onValueListItemDropped(event: CdkDragDrop<string[]>) {
    if (event.currentIndex === event.previousIndex) return;

    this.moveColumn(event.previousIndex, event.currentIndex);
  }
  private moveColumn(oldIndex: number, newIndex: number) {
    let movedValue = this.columns.splice(oldIndex, 1);
    this.columns.splice(newIndex, 0, ...movedValue);
    for (let index = 0; index < this.columns.length; index++) {
      this.columns[index].rank = index;
    }
  }
  private createItemPickerList() {
    this.itemPickerList = [];
    if (this.viewContext == ViewContext.People) {
      this.workspaceService.activeWorkspaceIsOrg.subscribe((result) => {
        if (
          result &&
          this.maxWorkspaceLimit > 0 &&
          !PeopleSystemColumns.find((x) => x.name == 'Workspaces')
        ) {
          PeopleSystemColumns.push(
            new ItemPickerModel(PeopleFilterFields.workspaces, '', 'Workspaces')
          );
        }
        this.itemPickerList = PeopleSystemColumns.filter(
          (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
        );
        if (this.appConfigService.tenant.usesCertifications) {
          this.itemPickerList.push(
            new ItemPickerModel(PeopleFilterFields.candidateId, '', 'CertMetrics Candidate Id')
          );
        }
        this.itemPickerList = this.itemPickerList.concat(
          this.attributes
            .filter(
              (f) =>
                f.entityType == AttributeEntityType.Person &&
                f.uiType != AttributeUiType.EncryptedField &&
                this.columns.findIndex((q) => q.customFieldId == f.id) == -1
            )
            .map((f) => new ItemPickerModel(f.id, '', f.name))
        );
      });
    } else if (this.viewContext == ViewContext.TeamRosters) {
      this.workspaceService.activeWorkspaceIsOrg.subscribe((result) => {
        if (result && !TeamRostersSystemColumns.find((x) => x.name == 'Team Workspace')) {
          TeamRostersSystemColumns.push(new ItemPickerModel('teamWorkspace', '', 'Team Workspace'));
        }
        this.itemPickerList = TeamRostersSystemColumns.filter(
          (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
        );
        this.itemPickerList = this.itemPickerList.concat(
          this.attributes
            .filter(
              (f) =>
                f.entityType != AttributeEntityType.Evaluation &&
                f.entityType != AttributeEntityType.Survey &&
                f.uiType != AttributeUiType.EncryptedField &&
                this.columns.findIndex((q) => q.customFieldId == f.id) == -1
            )
            .map((f) => new ItemPickerModel(f.id, '', f.name))
        );
      });
    } else if (this.viewContext == ViewContext.TeamBench) {
      this.workspaceService.activeWorkspaceIsOrg.subscribe((result) => {
        if (result && !TeamRostersSystemColumns.find((x) => x.name == 'Expressed Interest')) {
          TeamRostersSystemColumns.push(
            new ItemPickerModel('seatUpdatedOn', '', 'Expressed Interest')
          );
        }
        this.itemPickerList = TeamRostersSystemColumns.filter(
          (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
        );
        this.itemPickerList = this.itemPickerList.concat(
          this.attributes
            .filter(
              (f) =>
                f.entityType != AttributeEntityType.Evaluation &&
                f.entityType != AttributeEntityType.Survey &&
                f.entityType != AttributeEntityType.Team &&
                f.uiType != AttributeUiType.EncryptedField &&
                this.columns.findIndex((q) => q.customFieldId == f.id) == -1
            )
            .map((f) => new ItemPickerModel(f.id, '', f.name))
        );
      });
    } else if (this.viewContext == ViewContext.Teams) {
      this.workspaceService.activeWorkspaceIsOrg.subscribe((result) => {
        if (result && !TeamSystemColumns.find((x) => x.name == 'Workspace')) {
          TeamSystemColumns.push(new ItemPickerModel('workspace', '', 'Workspace'));
        }
        this.itemPickerList = TeamSystemColumns.filter(
          (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
        );
        this.itemPickerList = this.itemPickerList.concat(
          this.attributes
            .filter(
              (f) =>
                f.entityType == AttributeEntityType.Team &&
                f.uiType != AttributeUiType.EncryptedField &&
                this.columns.findIndex((q) => q.customFieldId == f.id) == -1
            )
            .map((f) => new ItemPickerModel(f.id, '', f.name))
        );
      });
    } else if (this.viewContext == ViewContext.TeamSurvey) {
      this.itemPickerList = TeamSurveyDetailsReportSystemColumns.filter(
        (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
      );
      this.itemPickerList = this.itemPickerList.concat(
        this.attributes
          .filter((f) => this.columns.findIndex((q) => q.customFieldId == f.id) == -1)
          .map((f) => new ItemPickerModel(f.id, '', f.name))
      );
    } else if (this.viewContext == ViewContext.Forms) {
      this.itemPickerList = FormDetailsReportSystemColumns.filter(
        (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
      );
    } else if (
      this.viewContext == ViewContext.AllCredentialingDetailsReport ||
      this.viewContext == ViewContext.CredentialingDetailsReport
    ) {
      this.itemPickerList = CredentialingDetailsReportSystemColumns.filter(
        (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
      );
    } else if (
      this.viewContext == ViewContext.CredentialPaymentRecordsReport ||
      this.viewContext == ViewContext.AllPaymentRecordsReport
    ) {
      const columns = this.appConfigService.tenant.modules.find(
        (x) => x.moduleType === TenantModuleType.Credentials
      )
        ? CredentialPaymentRecordsReportSystemColumns
        : PaymentRecordsReportSystemColumns;

      this.itemPickerList = columns.filter(
        (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
      );
    } else if (this.viewContext === ViewContext.AllInvoiceReport) {
      this.itemPickerList = AllInvoiceReportSystemColumns.filter(
        (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
      );
    } else if (this.viewContext === ViewContext.OpportunityBoardReport) {
      this.itemPickerList = OpportunityBoardReportSystemColumns.filter(
        (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
      );
    } else if (this.viewContext === ViewContext.PersonScenarioReport) {
      this.itemPickerList = PersonScenarioReportSystemColumns.filter(
        (f) => this.columns.findIndex((q) => q.systemName == f.id) == -1
      );
    }
  }

  onDeleteColumn(column: CustomViewColumn) {
    this.columns.splice(this.columns.indexOf(column), 1);
    for (let index = 0; index < this.columns.length; index++) {
      this.columns[index].rank = index;
    }
    this.createItemPickerList();
  }
  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
